import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { ServiceIcon } from '../components/ServiceIcon';
import { services } from '../data';

// Map service IDs to their product page URLs
const serviceUrls: Record<number, string> = {
  1: '/produk-layanan',
  2: '/produk-layanan/market-marketing-research',
  3: '/produk-layanan/finance-tax-accounting',
  4: '/produk-layanan/business-proposal-pitch-deck',
  5: '/produk-layanan/business-advisory',
  6: '/produk-layanan/business-it-service',
  7: '/produk-layanan/business-strategy-audit',
  8: '/produk-layanan/human-resource-planning-training',
  9: '/produk-layanan/industry-economic-analysis',
};

export function Services() {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section
      id="services"
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
        >
          <source src="/services-bg.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Dark Overlay Filter */}
      <div className="absolute inset-0 z-10 bg-dark-900/80" />

      {/* Subtle Gradient Overlay */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-dark-900/50 via-transparent to-dark-900/70" />

      <div className="relative z-20 max-w-7xl mx-auto section-padding">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6"
          >
            <span className="w-2 h-2 bg-primary-400 rounded-full" />
            <span className="text-sm font-medium text-white/90">Layanan Kami</span>
          </motion.div>

          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Solusi Lengkap untuk
            <span className="text-gradient"> Kesuksesan Bisnis</span>
          </h2>

          <p className="text-lg text-white/70">
            Kami menawarkan berbagai layanan profesional yang dirancang untuk membantu bisnis Anda
            berkembang dan mencapai potensi maksimal.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch"
        >
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              variants={itemVariants}
              whileHover={{ y: -12, scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <Link
                to={serviceUrls[service.id] || `/produk-layanan/${service.id}`}
                className="group relative glass rounded-3xl p-8 border border-white/50 shadow-lg hover:shadow-2xl hover:shadow-primary-500/10 transition-all duration-500 overflow-hidden block h-full"
              >
                {/* Hover Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary-600/0 to-primary-600/0 group-hover:from-primary-600/5 group-hover:to-primary-600/10 transition-all duration-500" />

                {/* Glow Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: 'radial-gradient(600px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(37, 99, 235, 0.06), transparent 40%)',
                  }}
                />

                <div className="relative h-full flex flex-col">
                  {/* Icon */}
                  <div className="mb-6">
                    <motion.div
                      whileHover={{ rotate: 5, scale: 1.1 }}
                      className="w-16 h-16 bg-gradient-to-br from-primary-900/50 to-primary-800/30 group-hover:from-primary-600 group-hover:to-primary-700 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/10 group-hover:shadow-primary-500/30 transition-all duration-300"
                    >
                      <ServiceIcon
                        name={service.icon}
                        className="w-8 h-8 text-primary-400 group-hover:text-white transition-colors duration-300"
                      />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-primary-400 transition-colors duration-300"
                  >
                    {service.title}
                  </h3>

                  <p className="text-white/70 leading-relaxed mb-6 flex-grow">{service.description}</p>

                  {/* Link */}
                  <motion.div
                    className="flex items-center gap-2 text-primary-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    initial={{ x: -10 }}
                    whileHover={{ x: 0 }}
                  >
                    <span>Pelajari</span>
                    <ArrowUpRight className="w-4 h-4" />
                  </motion.div>

                  {/* Number Badge */}
                  <div className="absolute top-6 right-6">
                    <span className="text-5xl font-display font-bold text-primary-100/50 group-hover:text-primary-200/70 transition-colors duration-300"
                    >
                      0{index + 1}
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <p className="text-dark-600 mb-6">
            Butuh solusi kustom untuk bisnis Anda?
          </p>
          <motion.a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-green-700 text-white font-semibold rounded-full hover:bg-green-800 transition-colors duration-300 shadow-xl shadow-green-700/30"
          >
            Konsultasi Gratis
            <ArrowUpRight className="w-5 h-5" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}
